import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { Chip } from '../ui/Chip';
import { RangeSlider } from '../ui/RangeSlider';
import { SegmentedControl } from '../ui/SegmentedControl';
import { actTypes, genres } from '../../data/options';
import { baht } from '../../utils/format';
import { spring } from '../../utils/motion';
import type { OrganiserPrefs } from '../../types/marketplace';
interface OrganiserPreferencesFormProps {
  value: OrganiserPrefs;
  onChange: (value: OrganiserPrefs) => void;
}
const toggle = <T,>(list: T[], item: T) => list.includes(item) ? list.filter(x => x !== item) : [...list, item];
export function OrganiserPreferencesForm({
  value,
  onChange
}: OrganiserPreferencesFormProps) {
  const [keyInput, setKeyInput] = useState('');
  const addKey = () => {
    const k = keyInput.trim();
    if (k && !value.mustHaveKeys.includes(k)) onChange({
      ...value,
      mustHaveKeys: [...value.mustHaveKeys, k]
    });
    setKeyInput('');
  };
  return <div className="space-y-6" data-id="element-534" data-mp="o2jtf5ykvhn7">
      <div data-id="element-535" data-mp="jain00o3cl53">
        <p className="mb-3 text-[15px] font-bold" data-id="element-536" data-mp="ja0h0cbmmsjz">Genres you book</p>
        <div className="flex flex-wrap gap-2" data-id="element-537" data-mp="j8pcube9risc">
          {genres.map(g => <Chip key={g} selected={value.genres.includes(g)} onClick={() => onChange({
          ...value,
          genres: toggle(value.genres, g)
        })} data-id="element-538" data-mp="ebp7y3t5om2t">
              {g}
            </Chip>)}
        </div>
      </div>
      <div data-id="element-539" data-mp="16y0hi3yqpxbr">
        <p className="mb-3 text-[15px] font-bold" data-id="element-540" data-mp="16xqpyby1ri3y">Act types</p>
        <div className="flex flex-wrap gap-2" data-id="element-541" data-mp="121jigeq05yjw">
          {actTypes.map(t => <Chip key={t} selected={value.actTypes.includes(t)} onClick={() => onChange({
          ...value,
          actTypes: toggle(value.actTypes, t)
        })} data-id="element-542" data-mp="x0vuy30zfzuf">
              {t}
            </Chip>)}
        </div>
      </div>
      <RangeSlider label="Max budget per act" min={2000} max={100000} step={1000} value={value.budgetMax} onChange={budgetMax => onChange({
      ...value,
      budgetMax
    })} format={baht} data-id="element-543" data-mp="3hsxpsnnvvlxj" />
      <RangeSlider label="Max distance" min={5} max={250} step={5} value={value.maxDistanceKm} onChange={maxDistanceKm => onChange({
      ...value,
      maxDistanceKm
    })} format={v => `${v} km`} data-id="element-544" data-mp="3f8ric6029dc8" />
      <div data-id="element-545" data-mp="35bwjflzvlaxf">
        <p className="mb-3 text-[15px] font-bold" data-id="element-546" data-mp="3a8d94kmq3xcm">Minimum rating</p>
        <SegmentedControl<string> ariaLabel="Minimum rating" layoutId="min-rating" className="flex w-full max-w-sm" size="sm" value={String(value.minRating)} onChange={v => onChange({
        ...value,
        minRating: Number(v)
      })} options={[{
        value: '0',
        label: 'Any'
      }, {
        value: '4',
        label: '4.0+'
      }, {
        value: '4.5',
        label: '4.5+'
      }, {
        value: '4.8',
        label: '4.8+'
      }]} data-id="element-547" data-mp="3a6shp3vpguiz" />
      </div>
      <div data-id="element-548" data-mp="2dkqgkxsrsz4g">
        <p className="text-[15px] font-bold" data-id="element-549" data-mp="2hjy6kxks37i6">Must-have Keys</p>
        <p className="text-sm text-ink-3" data-id="element-550" data-mp="2hjfzt70hpaw0">Performers who know these songs get a boost.</p>
        <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-551" data-mp="1yw79ejc9cqbx">
          <AnimatePresence initial={false} data-id="element-552" data-mp="1tx40uox2k1z7">
            {value.mustHaveKeys.map(k => <motion.button key={k} layout type="button" initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={spring.snappy} onClick={() => onChange({
            ...value,
            mustHaveKeys: value.mustHaveKeys.filter(x => x !== k)
          })} aria-label={`Remove ${k}`} className="flex h-9 items-center gap-1 rounded-sm bg-ink pl-3 pr-2 text-sm font-bold text-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-553" data-mp="27wvbqgsah28z">
                {k}
                <XIcon className="h-3.5 w-3.5" data-id="element-554" data-mp="30ioljac05h59" />
              </motion.button>)}
          </AnimatePresence>
          <form onSubmit={e => {
          e.preventDefault();
          addKey();
        }} data-id="element-555" data-mp="3foatq0f8cji2">
            <input value={keyInput} onChange={e => setKeyInput(e.target.value)} placeholder="Add a song…" aria-label="Add a must-have song" className="h-9 w-40 rounded-sm border border-dashed border-line bg-transparent px-3 text-sm placeholder:text-ink-3 focus:border-ink focus:outline-none" data-id="element-556" data-mp="3jobgc8chg107" />
          </form>
        </div>
      </div>
    </div>;
}